import { ActionType } from 'typesafe-actions'
import {
  SOCIAL_MATCHES_REQUESTED,
  SOCIAL_MATCHES_COMPLETED,
  SOCIAL_MATCHES_FAILED,
  UPDATE_SOCIAL_MATCH_REQUESTED,
  UPDATE_SOCIAL_MATCH_COMPLETED,
  UPDATE_SOCIAL_MATCH_FAILED,
  SUBMIT_SOCIAL_MATCH_REQUESTED,
  SUBMIT_SOCIAL_MATCH_COMPLETED,
  SUBMIT_SOCIAL_MATCH_FAILED,
  DELETE_SOCIAL_MATCH_REQUESTED,
  DELETE_SOCIAL_MATCH_COMPLETED,
  DELETE_SOCIAL_MATCH_FAILED,
  CLEAR_LAST_SUBMITTED,
  RESET_SOCIAL_MATCHES,
} from 'actions/social-matches-actions'
import * as actions from 'actions/social-matches-actions'
import { MY_SOCIAL_MATCHES_KEY, SocialMatchUpUI } from 'types/global'
import { MatchUpSubmissionStateEnum } from 'types/api/enums'
import { SOCIAL_MATCHES_FETCH_LIMIT } from 'constants/profile'

type SocialMatchesAction = ActionType<typeof actions>
type SocialMatchDictionary = Record<string, SocialMatchUpUI>
type SocialMatchListings = Record<MatchUpSubmissionStateEnum, SocialMatchUpUI[]>
type SocialMatchStore = SocialMatchListings & { dict: SocialMatchDictionary }
type SocialMatchTotals = { [key in MatchUpSubmissionStateEnum]: number }

export type State = Readonly<{
  matchesUpdating: Record<string, boolean>
  errorSubmittingMatch: boolean
  isMatchSubmitting: boolean
  lastSubmitted: string
  errorLoadingMatches: Record<MatchUpSubmissionStateEnum, boolean>
  isLoadingMatches: Record<MatchUpSubmissionStateEnum, boolean>
  matchesLoaded: Record<string, SocialMatchStore>
  totals: Record<string, SocialMatchTotals>
}>

export const initialState: State = {
  matchesUpdating: {},
  errorSubmittingMatch: false,
  isMatchSubmitting: false,
  lastSubmitted: '',
  errorLoadingMatches: {
    APPROVED: false,
    PENDING: false,
    REJECTED: false,
  },
  isLoadingMatches: {
    APPROVED: false,
    PENDING: false,
    REJECTED: false,
  },
  matchesLoaded: {},
  totals: {},
}

export function createSocialMatchStore(): SocialMatchStore {
  return {
    dict: {},
    APPROVED: [],
    PENDING: [],
    REJECTED: [],
  }
}

function createTotals(): SocialMatchTotals {
  return {
    APPROVED: 0,
    PENDING: 0,
    REJECTED: 0,
  }
}

export function updateSocialMatchListings(store: SocialMatchStore) {
  store.APPROVED = []
  store.PENDING = []
  store.REJECTED = []
  const sortedDictionary = Object.values(store.dict).sort((a, b) => {
    /* sort matches by most recently played */
    let diff = -a.start.localeCompare(b.start)
    if (!diff) {
      /* if played on same day, sort by creation date - oldest to newest */
      diff = b.createdAt.localeCompare(a.createdAt)
    }
    return diff
  })
  sortedDictionary.forEach(match => store[match.submittedStatus].push(match))
  return store
}

// eslint-disable-next-line import/no-anonymous-default-export
export default function (state = initialState, action: SocialMatchesAction): State {
  switch (action.type) {
    case CLEAR_LAST_SUBMITTED:
      return { ...state, lastSubmitted: '' }
    case SOCIAL_MATCHES_REQUESTED:
      return { ...state, isLoadingMatches: { ...state.isLoadingMatches, [action.payload]: true } }
    case SOCIAL_MATCHES_COMPLETED: {
      const key: string = action.payload.playerKey
      const submissionStatus = action.payload.matchSubmissionState
      const currentMatchesForPlayer = state.matchesLoaded[key] || createSocialMatchStore()
      const currentTotalsForPlayer = state.totals[key] || createTotals()

      // Count current totals for matches loaded in UI state
      const loadedTotals = {
        APPROVED: currentMatchesForPlayer.APPROVED.length,
        PENDING: currentMatchesForPlayer.PENDING.length,
        REJECTED: currentMatchesForPlayer.REJECTED.length,
      }

      const totalsModifiers = {
        APPROVED: 0,
        PENDING: 0,
        REJECTED: 0,
      }

      // Add 4 most recently updated matches
      action.payload.recentlyUpdatedMatches.forEach(match => {
        const loadedMatch = currentMatchesForPlayer.dict[match.id]

        if (loadedMatch && loadedMatch.submittedStatus !== match.submittedStatus) {
          totalsModifiers[loadedMatch.submittedStatus]++
        }

        currentMatchesForPlayer.dict[match.id] = match
      })

      // Update match listings
      let updatedSocialMatchStore = updateSocialMatchListings(currentMatchesForPlayer)

      // Remove all matches exceeding previous total numbers
      for (let key in updatedSocialMatchStore) {
        let prevTotal = loadedTotals[key] - totalsModifiers[key]
        if (typeof prevTotal === 'number' && updatedSocialMatchStore[key]?.length > prevTotal) {
          let updatedMatches = updatedSocialMatchStore[key]
          for (let i = prevTotal; i < updatedMatches.length; i++) {
            const matchInExcess = updatedMatches[i]
            delete updatedSocialMatchStore.dict[matchInExcess.id]
          }
        }
      }

      // Finally add new social matches of requested type
      action.payload.paginatedMatches.forEach(match => {
        updatedSocialMatchStore.dict[match.id] = match
      })
      updatedSocialMatchStore = updateSocialMatchListings(updatedSocialMatchStore)

      // If store goes out of sync we need to make sure we load only 4 new matches at a time - the store
      // goes out of sync when matches get deleted by another user, resulting in more matches loaded
      // in UI state than there actually are on the server
      if (
        updatedSocialMatchStore[submissionStatus].length - loadedTotals[submissionStatus] >
        SOCIAL_MATCHES_FETCH_LIMIT
      ) {
        const matchesInExcess =
          updatedSocialMatchStore[submissionStatus].length -
          loadedTotals[submissionStatus] -
          SOCIAL_MATCHES_FETCH_LIMIT

        for (let i = 0; i < matchesInExcess; i++) {
          const matchInExcess = updatedSocialMatchStore[submissionStatus].pop()
          if (matchInExcess) delete updatedSocialMatchStore.dict[matchInExcess.id]
        }
      }
      return {
        ...state,
        isLoadingMatches: {
          ...state.isLoadingMatches,
          [action.payload.matchSubmissionState]: false,
        },
        errorLoadingMatches: {
          ...state.errorLoadingMatches,
          [action.payload.matchSubmissionState]: false,
        },
        matchesLoaded: {
          ...state.matchesLoaded,
          [key]: {
            ...updatedSocialMatchStore,
          },
        },
        totals: { ...state.totals, [key]: { ...currentTotalsForPlayer, ...action.payload.totals } },
      }
    }
    case SOCIAL_MATCHES_FAILED:
      return {
        ...state,
        isLoadingMatches: { ...state.isLoadingMatches, [action.payload]: false },
        errorLoadingMatches: {
          ...state.errorLoadingMatches,
          [action.payload]: true,
        },
      }
    case RESET_SOCIAL_MATCHES: {
      const key = MY_SOCIAL_MATCHES_KEY
      const submissionStatus = action.payload
      const currentMatchesForPlayer = state.matchesLoaded[key] || createSocialMatchStore()
      for (let id in currentMatchesForPlayer.dict) {
        if (currentMatchesForPlayer.dict[id].submittedStatus === submissionStatus)
          delete currentMatchesForPlayer.dict[id]
      }

      return {
        ...state,
        matchesLoaded: {
          ...state.matchesLoaded,
          [key]: {
            ...currentMatchesForPlayer,
            [submissionStatus]: [],
          },
        },
      }
    }
    case UPDATE_SOCIAL_MATCH_REQUESTED:
      return { ...state, matchesUpdating: { ...state.matchesUpdating, [action.payload]: true } }
    case UPDATE_SOCIAL_MATCH_FAILED:
      const { matchId, shouldRemoveMatch } = action.payload

      delete state.matchesUpdating[matchId]

      if (shouldRemoveMatch) {
        const key = MY_SOCIAL_MATCHES_KEY
        const currentMatchesForPlayer = state.matchesLoaded[key] || createSocialMatchStore()
        delete currentMatchesForPlayer.dict[matchId]
        const updatedSocialMatchStore = updateSocialMatchListings(currentMatchesForPlayer)

        return {
          ...state,
          matchesUpdating: { ...state.matchesUpdating },
          matchesLoaded: {
            ...state.matchesLoaded,
            [key]: {
              ...updatedSocialMatchStore,
            },
          },
        }
      } else {
        return { ...state, matchesUpdating: { ...state.matchesUpdating } }
      }
    case UPDATE_SOCIAL_MATCH_COMPLETED: {
      const key = MY_SOCIAL_MATCHES_KEY
      const currentMatchesForPlayer = state.matchesLoaded[key] || createSocialMatchStore()
      const currentTotalsForPlayer = state.totals[key] || createTotals()
      delete state.matchesUpdating[action.payload.id]
      currentMatchesForPlayer.dict[action.payload.id] = action.payload
      const updatedSocialMatchStore = updateSocialMatchListings(currentMatchesForPlayer)
      if (
        updatedSocialMatchStore[action.payload.submittedStatus].length > SOCIAL_MATCHES_FETCH_LIMIT
      ) {
        const matchInExcess = updatedSocialMatchStore[action.payload.submittedStatus].pop()
        if (matchInExcess) delete currentMatchesForPlayer.dict[matchInExcess.id]
      }
      return {
        ...state,
        matchesUpdating: { ...state.matchesUpdating },
        matchesLoaded: {
          ...state.matchesLoaded,
          [key]: {
            ...updatedSocialMatchStore,
          },
        },
        totals: {
          ...state.totals,
          [key]: {
            ...currentTotalsForPlayer,
            PENDING: currentTotalsForPlayer.PENDING - 1,
            [action.payload.submittedStatus]:
              currentTotalsForPlayer[action.payload.submittedStatus] + 1,
          },
        },
      }
    }
    case SUBMIT_SOCIAL_MATCH_REQUESTED:
      return { ...state, isMatchSubmitting: true }
    case SUBMIT_SOCIAL_MATCH_FAILED:
      return { ...state, isMatchSubmitting: false, errorSubmittingMatch: true }
    case SUBMIT_SOCIAL_MATCH_COMPLETED: {
      const key = MY_SOCIAL_MATCHES_KEY
      const currentMatchesForPlayer = state.matchesLoaded[key] || createSocialMatchStore()
      const currentTotalsForPlayer = state.totals[key] || createTotals()
      currentMatchesForPlayer.dict[action.payload.id] = action.payload
      const updatedSocialMatchStore = updateSocialMatchListings(currentMatchesForPlayer)
      const matchInExcess = updatedSocialMatchStore[action.payload.submittedStatus].pop()
      if (matchInExcess) delete currentMatchesForPlayer.dict[matchInExcess.id]
      return {
        ...state,
        isMatchSubmitting: false,
        errorSubmittingMatch: false,
        lastSubmitted: action.payload.id,
        matchesLoaded: {
          ...state.matchesLoaded,
          [key]: {
            ...updatedSocialMatchStore,
          },
        },
        totals: {
          ...state.totals,
          [key]: {
            ...currentTotalsForPlayer,
            PENDING: currentTotalsForPlayer.PENDING + 1,
          },
        },
      }
    }

    case DELETE_SOCIAL_MATCH_REQUESTED:
      return { ...state, matchesUpdating: { ...state.matchesUpdating, [action.payload]: true } }
    case DELETE_SOCIAL_MATCH_FAILED:
      delete state.matchesUpdating[action.payload]
      return { ...state, matchesUpdating: { ...state.matchesUpdating } }
    case DELETE_SOCIAL_MATCH_COMPLETED: {
      const key = MY_SOCIAL_MATCHES_KEY
      const currentMatchesForPlayer = state.matchesLoaded[key] || createSocialMatchStore()
      const currentTotalsForPlayer = state.totals[key] || createTotals()
      const submittedStatusOfDeleted = currentMatchesForPlayer.dict[action.payload].submittedStatus
      delete currentMatchesForPlayer.dict[action.payload]
      const updatedSocialMatchStore = updateSocialMatchListings(currentMatchesForPlayer)
      delete state.matchesUpdating[action.payload]
      return {
        ...state,
        matchesUpdating: { ...state.matchesUpdating },
        matchesLoaded: {
          ...state.matchesLoaded,
          [key]: {
            ...updatedSocialMatchStore,
          },
        },
        totals: {
          ...state.totals,
          [key]: {
            ...currentTotalsForPlayer,
            [submittedStatusOfDeleted]: currentTotalsForPlayer[submittedStatusOfDeleted] - 1,
          },
        },
      }
    }
    default:
      return state
  }
}
